import FetchWrapper from '../../Network/FetchWrapper'
import MemberRepo from './MemberRepo'
import MemberInvitationDTO from '../DTO/MemberInvitationDTO'
import MemberRegisterInformationDTO from '../DTO/MemberRegisterInformationDTO'
import {DTO} from '../../Common/DtoUtilities'
import {SignboardDTO, SignboardJSON} from '../DTO/SignboardDTO'
import {MemberSelfDTO, MemberSelfJSON} from '../DTO/MemberSelfDTO'
import {CommonResponseDTO, CommonResponseJSON} from '../../Network/CommonResponse'
import {MemberAddressDTO} from '../DTO/MemberAddressDTO'
import {Nullable} from '../../Common/TypeHelper'
import {InvitationInfoDTO, InvitationInfoJSON} from '../DTO/InvitationInfoDTO'
import {MemberMailUpdateDTO} from '../DTO/MemberMailUpdateDTO'
import {MemberJoinBucketDTO} from '../DTO/MemberJoinBucketDTO'
import {PaymentMethodDTO, PaymentMethodJSON} from '../DTO/PaymentMethodDTO'
import {BucketInfoDTO, BucketInfoJSON} from '../DTO/BucketInfoDTO'
import {MemberBucketOptionsDTO, MemberBucketOptionsJSON} from '../DTO/MemberBucketOptionsDTO'
import {BucketLeaveDTO, BucketLeaveJSON} from '../DTO/BucketLeaveDTO'
import {BucketRegisterResponseDataDTO, BucketRegisterResponseDataJSON, FreeBucketRegisterResponseDataDTO, FreeBucketRegisterResponseDataJSON, isFreeBucketResponseJSON} from '../DTO/BucketRegisterResponseDataDTO'
import {MemberContractInfoDTO, MemberContractInfoJSON} from '../DTO/MemberContractInfoDTO'
import {withMembersAuth} from '../../Network/BrowserFetchWrapper'
import {MemberVerifyIdentityDTO} from '../DTO/MemberVerifyIdentityDTO'
import {MemberVerifyIdentityInfoDTO, MemberVerifyIdentityInfoJSON} from '../DTO/MemberVerifyIdentityInfoDTO'
import {PreMemberRegisterInformationDTO} from '../DTO/PreMemberRegisterInformationDTO'
import {MobileCarrierStartPaymentInfo, MobileCarrierStartPaymentInfoDTO} from '../DTO/MobileCarrierStartPaymentInfoDTO'
import {MemberRegisterResultDTO, MemberRegisterResultJSON} from '../DTO/MemberRegisterResultDTO'
import {ConvenienceStoreKey, UsingMyCreditCardInfo} from '../Props/ContractInputInfo'
import {UsingMyCreditCardInfoDTO} from '../DTO/UsingMyCreditCardInfoDTO'
import {MemberPreJoinBucketDTO} from '../DTO/MemberPreJoinBucketDTO'
import {SignupPreRegisterInfoDTO, SignupPreRegisterInfoJSON} from '../DTO/SignupPreRegisterInfoDTO'
import {CvsContractDataJSON, CvsContractDTO, isCvsContractResponseJSON} from '../DTO/CvsContractDTO'
import {JoinPreRegisterInfoDTO, JoinPreRegisterInfoJSON} from '../DTO/JoinPreRegisterInfoDTO'
import {MemberShippingAddressDTO, MemberShippingAddressInfoDTO, ShippingAddressInfoJSON} from '../DTO/MemberShippingAddressDTO'
import {SignedUpBucketInfoDTO, SignedUpBucketInfoJSON} from '../DTO/SignedUpBucketInfoDTO'
import {MemberEditContractDTO} from '../DTO/MemberEditContractDTO'
import {ContractChangeResultDTO, ContractChangeResultJSON} from '../DTO/ContractChangeResultDTO'
import {MemberResetPasswordDTO} from '../DTO/MemberResetPasswordDTO'
import BucketBannerDTO, { BucketBannerJSON } from '../DTO/BucketBannerDTO'
import { MemberMetaDataDTO, MemberMetaDataJSON, MemberMetaDataValue } from '../DTO/MemberMetaDataDTO'

class NetworkMemberRepo implements MemberRepo {

  private fetchWrapper: FetchWrapper
  private serverUrl = process.env.REACT_APP_MEMBUCKET_ADMIN_APP_SERVER_URL

  constructor(fetchWrapper: FetchWrapper) {
    this.fetchWrapper = fetchWrapper
  }

  sendOneTimeUrlMail(
    memberInvitationDTO: MemberInvitationDTO,
  ): Promise<CommonResponseDTO<null>> {
    return this.fetchWrapper.postJson(`${this.serverUrl}/v1/m/p/members/invitation`,
      DTO.toObject(memberInvitationDTO),
    ).then((response: CommonResponseJSON<null>) => {
      return CommonResponseDTO.from(
        response,
        CommonResponseDTO.NO_CONVERT,
      )
    })
  }

  getInvitationInfo(invitationId: string): Promise<CommonResponseDTO<Nullable<InvitationInfoDTO>>> {
    return this.fetchWrapper.getByJson(`${this.serverUrl}/v1/m/p/members/${invitationId}`)
      .then((response: CommonResponseJSON<InvitationInfoJSON>) => {
        return CommonResponseDTO.from(
          response,
          InvitationInfoDTO.fromJSON,
        )
      })
  }

  getSignboardBySignupKey(signupKey: string): Promise<CommonResponseDTO<Nullable<SignboardDTO>>> {
    return this.fetchWrapper.getByJson(`${this.serverUrl}/v1/m/p/signboards/${signupKey}`)
      .then((response: CommonResponseJSON<SignboardJSON>) => {
        return CommonResponseDTO.from(
          response,
          SignboardDTO.fromJSON,
        )
      })
  }

  postCreateMember(
    memberRegisterInformationDTO: MemberRegisterInformationDTO,
  ): Promise<CommonResponseDTO<Nullable<BucketRegisterResponseDataDTO |
    CvsContractDTO |
    FreeBucketRegisterResponseDataDTO>>> {
    return this.fetchWrapper.postJson(`${this.serverUrl}/v1/m/p/members`,
      DTO.toObject(memberRegisterInformationDTO),
    ).then((
      response: CommonResponseJSON<BucketRegisterResponseDataJSON |
        CvsContractDataJSON |
        FreeBucketRegisterResponseDataJSON>,
    ) => {
      if (isCvsContractResponseJSON(response)) {
        return CommonResponseDTO.from(response, CvsContractDTO.fromJSON)
      }
      if (isFreeBucketResponseJSON(response)) {
        return CommonResponseDTO.from(response, FreeBucketRegisterResponseDataDTO.fromJSON)
      }

      return CommonResponseDTO.from(
        response as CommonResponseJSON<BucketRegisterResponseDataJSON>,
        BucketRegisterResponseDataDTO.fromJSON,
      )
    })
  }

  postCreatePreMember(
    preMemberRegisterInformationDTO: PreMemberRegisterInformationDTO,
  ): Promise<CommonResponseDTO<Nullable<MobileCarrierStartPaymentInfoDTO>>> {
    return this.fetchWrapper.postJson(`${this.serverUrl}/v1/m/p/premembers`,
      DTO.toObject(preMemberRegisterInformationDTO),
    ).then((response: CommonResponseJSON<MobileCarrierStartPaymentInfoDTO>) => {
      return CommonResponseDTO.from(
        response,
        MobileCarrierStartPaymentInfoDTO.fromJSON,
      )
    })
  }

  getSignupPreRegisterInfo(
    temporaryEnrollmentId: string,
  ): Promise<CommonResponseDTO<Nullable<SignupPreRegisterInfoDTO>>> {
    return this.fetchWrapper.getByJson(`${this.serverUrl}/v1/m/p/premembers/${temporaryEnrollmentId}`)
      .then((response: CommonResponseJSON<SignupPreRegisterInfoJSON>) => {
        return CommonResponseDTO.from(
          response,
          SignupPreRegisterInfoDTO.fromJSON,
        )
      })
  }

  getSignupComplete(
    temporaryEnrollmentId: string,
  ): Promise<CommonResponseDTO<Nullable<MemberRegisterResultDTO>>> {
    return this.fetchWrapper.getByJson(`${this.serverUrl}/v1/m/p/members/registration/${temporaryEnrollmentId}`, {})
      .then((response: CommonResponseJSON<MemberRegisterResultJSON>) => {
        return CommonResponseDTO.from(
          response,
          MemberRegisterResultDTO.fromJSON,
        )
      })
  }

  getEditContractResult(
    temporaryEnrollmentId: string,
  ): Promise<CommonResponseDTO<Nullable<ContractChangeResultDTO>>> {
    return this.fetchWrapper.getByJson(
      `${this.serverUrl}/v1/m/r/buckets/${temporaryEnrollmentId}`, withMembersAuth(),
    )
      .then((response: CommonResponseJSON<ContractChangeResultJSON>) => {
        return CommonResponseDTO.from(
          response,
          ContractChangeResultDTO.fromJSON,
        )
      })
  }

  postOrderForUpdateMail(memberMailUpdateDTO: MemberMailUpdateDTO)
    : Promise<CommonResponseDTO<null>> {
    return this.fetchWrapper.postJson(`${this.serverUrl}/v1/m/r/members/self/mail/update`,
      DTO.toObject(memberMailUpdateDTO), withMembersAuth(),
    ).then((response: CommonResponseJSON<null>) => {
      return new CommonResponseDTO(
        response.status,
        response.data,
        response.errorMessageList,
      )
    })
  }

  putMailUpdateComplete(mailUpdateId: string): Promise<CommonResponseDTO<null>> {
    return this.fetchWrapper.putJson(`${this.serverUrl}/v1/m/r/members/self/mail/update/${mailUpdateId}`,
      {}, withMembersAuth(),
    ).then((response: CommonResponseJSON<null>) => CommonResponseDTO.ofNullData(response))
  }

  putPassword(password: string): Promise<CommonResponseDTO<null>> {
    return this.fetchWrapper.putJson(`${this.serverUrl}/v1/m/r/members/self/password/update`,
      {password: password}, withMembersAuth(),
    ).then((response: CommonResponseJSON<null>) => CommonResponseDTO.ofNullData(response))
  }

  putAddress(
    memberAddressDTO: MemberAddressDTO,
    sendMail: boolean,
  ): Promise<CommonResponseDTO<null>> {
    return this.fetchWrapper.putJson(`${this.serverUrl}/v1/m/r/members/self/address`,
      {
        ...DTO.toObject(memberAddressDTO),
        sendMail: sendMail,
      },
      withMembersAuth(),
    ).then((response: CommonResponseJSON<null>) => CommonResponseDTO.ofNullData(response))
  }

  putSubscribeMailMagazineIdList(
    bucketKey: string,
    subscribeMailMagazineIdList: number[],
  ): Promise<CommonResponseDTO<null>> {
    return this.fetchWrapper.putJson(
      `${this.serverUrl}/v1/m/r/members/self/${bucketKey}/bucket_options`,
      {subscribeMailMagazineIdList: subscribeMailMagazineIdList},
      withMembersAuth(),
    ).then((response: CommonResponseJSON<null>) => CommonResponseDTO.ofNullData(response))
  }

  getSelf(): Promise<CommonResponseDTO<MemberSelfDTO>> {
    return this.fetchWrapper.getByJson(`${this.serverUrl}/v1/m/r/members/self`, withMembersAuth())
      .then((response: CommonResponseJSON<MemberSelfJSON>) => {
        return CommonResponseDTO.from(
          response,
          MemberSelfDTO.fromJSON,
        )
      })
  }

  getSelfMetaData(metaDataId: number): Promise<CommonResponseDTO<Nullable<MemberMetaDataDTO>>> {
    return this.fetchWrapper.getByJson(
      `${this.serverUrl}/v1/m/r/members/self/meta_data/${metaDataId}`,
      withMembersAuth(),
    ).then((response: CommonResponseJSON<MemberMetaDataJSON>) => {
      return CommonResponseDTO.from(
        response,
        MemberMetaDataDTO.fromJSON,
      )
    })
  }

  putSelfMetaData(
    metaDataId: number,
    memberMetaDataList: MemberMetaDataValue[],
  ): Promise<CommonResponseDTO<null>> {
    return this.fetchWrapper.putJson(
      `${this.serverUrl}/v1/m/r/members/self/meta_data/${metaDataId}/update`,
      { memberMetaDataList },
      withMembersAuth(),
    ).then((response: CommonResponseJSON<null>) => CommonResponseDTO.ofNullData(response))
  }

  getSelfCreditCards(): Promise<CommonResponseDTO<UsingMyCreditCardInfoDTO[]>> {
    return this.fetchWrapper.getByJson(`${this.serverUrl}/v1/m/r/members/self/creditcards`, withMembersAuth())
      .then((response: CommonResponseJSON<UsingMyCreditCardInfo[]>) =>
        CommonResponseDTO.fromArray(response, UsingMyCreditCardInfoDTO.fromJSON))
  }

  putBucketForLeave(
    bucketKey: string,
    bucketLeaveReason: string,
  ): Promise<CommonResponseDTO<null>> {
    return this.fetchWrapper.putJson(
      `${this.serverUrl}/v1/m/r/members/self/buckets/${bucketKey}/leave`,
      {bucketLeaveReason: bucketLeaveReason},
      withMembersAuth(),
    ).then((response: CommonResponseJSON<null>) => CommonResponseDTO.ofNullData(response))
  }

  postBelongToBucket(memberJoinBucketDTO: MemberJoinBucketDTO):
    Promise<CommonResponseDTO<Nullable<BucketRegisterResponseDataDTO |
      CvsContractDTO |
      FreeBucketRegisterResponseDataDTO>>> {
    return this.fetchWrapper.postJson(`${this.serverUrl}/v1/m/r/members/self/bucket`,
      DTO.toObject(memberJoinBucketDTO), withMembersAuth(),
    ).then((response: CommonResponseJSON<BucketRegisterResponseDataJSON |
      CvsContractDataJSON |
      FreeBucketRegisterResponseDataJSON>,
    ) => {

      if (isCvsContractResponseJSON(response)) {
        return CommonResponseDTO.from(response, CvsContractDTO.fromJSON)
      }
      if (isFreeBucketResponseJSON(response)) {
        return CommonResponseDTO.from(response, FreeBucketRegisterResponseDataDTO.fromJSON)
      }

      return CommonResponseDTO.from(
        response as CommonResponseJSON<BucketRegisterResponseDataJSON>,
        BucketRegisterResponseDataDTO.fromJSON,
      )
    })
  }

  postPreBelongToBucket(
    memberPreJoinBucketDTO: MemberPreJoinBucketDTO,
  ): Promise<CommonResponseDTO<Nullable<MobileCarrierStartPaymentInfoDTO>>> {
    return this.fetchWrapper.postJson(`${this.serverUrl}/v1/m/r/members/self/prebucket`,
      DTO.toObject(memberPreJoinBucketDTO), withMembersAuth(),
    ).then((response: CommonResponseJSON<MobileCarrierStartPaymentInfoDTO>) => {
      return CommonResponseDTO.from(
        response,
        MobileCarrierStartPaymentInfoDTO.fromJSON,
      )
    })
  }

  getJoinPreRegisterInfo(
    temporaryEnrollmentId: string,
  ): Promise<CommonResponseDTO<Nullable<JoinPreRegisterInfoDTO>>> {
    return this.fetchWrapper.getByJson(
      `${this.serverUrl}/v1/m/r/members/self/prebucket/${temporaryEnrollmentId}`,
      withMembersAuth(),
    )
      .then((response: CommonResponseJSON<JoinPreRegisterInfoJSON>) => {
        return CommonResponseDTO.from(
          response,
          JoinPreRegisterInfoDTO.fromJSON,
        )
      })
  }

  getBelongToBucketComplete(
    temporaryEnrollmentId: string,
  ): Promise<CommonResponseDTO<Nullable<MemberRegisterResultDTO>>> {
    return this.fetchWrapper.getByJson(`${this.serverUrl}/v1/m/r/members/self/bucket/join/${temporaryEnrollmentId}`,
      withMembersAuth(),
    ).then((response: CommonResponseJSON<MemberRegisterResultJSON>) => {
      return CommonResponseDTO.from(
        response,
        MemberRegisterResultDTO.fromJSON,
      )
    })
  }

  putBucketContractInfo(
    memberEditContractDTO: MemberEditContractDTO,
  ): Promise<CommonResponseDTO<Nullable<MobileCarrierStartPaymentInfoDTO>>> {
    return this.fetchWrapper.putJson(`${this.serverUrl}/v1/m/r/members/self/contracts/${memberEditContractDTO.bucketKey}`,
      {...memberEditContractDTO.contractInputInfo},
      withMembersAuth(),
    ).then((response: CommonResponseJSON<MobileCarrierStartPaymentInfo>) => {
      return CommonResponseDTO.from(
        response,
        MobileCarrierStartPaymentInfoDTO.fromJSON,
      )
    })
  }

  getPaymentMethodListBySignupKey(signupKey: string)
    : Promise<CommonResponseDTO<PaymentMethodDTO[]>> {
    return this.fetchWrapper.getByJson(`${this.serverUrl}/v1/m/p/paymentmethods/${signupKey}`)
      .then((response: CommonResponseJSON<PaymentMethodJSON[]>) => {
        return CommonResponseDTO.fromArray(
          response,
          PaymentMethodDTO.fromJSON,
        )
      })
  }

  getBucketInfoByBucketKey(
    bucketKey: string,
  ): Promise<CommonResponseDTO<Nullable<BucketInfoDTO>>> {
    return this.fetchWrapper.getByJson(`${this.serverUrl}/v1/m/p/buckets/${bucketKey}`)
      .then((response: CommonResponseJSON<BucketInfoJSON & { key: string }>) => {
        const {status, data, errorMessageList} = response
        return CommonResponseDTO.from(
          {
            status: status,
            data: {...data, key: bucketKey},
            errorMessageList: errorMessageList,
          },
          BucketInfoDTO.fromJSON,
        )
      })
  }

  getMemberBucketOptionsByBucketKey(
    bucketKey: string,
  ): Promise<CommonResponseDTO<Nullable<MemberBucketOptionsDTO>>> {
    return this.fetchWrapper.getByJson(
      `${this.serverUrl}/v1/m/r/members/self/${bucketKey}/bucket_options`, withMembersAuth())
      .then((response: CommonResponseJSON<MemberBucketOptionsJSON>) => {
        return CommonResponseDTO.from(
          response,
          MemberBucketOptionsDTO.fromJSON,
        )
      })
  }

  getBucketLeaveByBucketKey(
    bucketKey: string,
  ): Promise<CommonResponseDTO<Nullable<BucketLeaveDTO>>> {
    return this.fetchWrapper.getByJson(
      `${this.serverUrl}/v1/m/r/members/self/buckets/${bucketKey}/leave`, withMembersAuth())
      .then((response: CommonResponseJSON<BucketLeaveJSON>) => {
        return CommonResponseDTO.from(
          response,
          BucketLeaveDTO.fromJSON,
        )
      })
  }

  getContractInfoByBucketKey(
    bucketKey: string,
  ): Promise<CommonResponseDTO<Nullable<MemberContractInfoDTO>>> {
    return this.fetchWrapper.getByJson(
      `${this.serverUrl}/v1/m/r/members/self/contracts/${bucketKey}`, withMembersAuth())
      .then((response: CommonResponseJSON<MemberContractInfoJSON>) => {
        return CommonResponseDTO.from(
          response,
          MemberContractInfoDTO.fromJSON,
        )
      })
  }

  postLogout(): Promise<CommonResponseDTO<null>> {
    return this.fetchWrapper.postJson(
      `${this.serverUrl}/v1/m/r/members/self/logout`, {}, withMembersAuth())
      .then((response: CommonResponseJSON<null>) => CommonResponseDTO.ofNullData(response))
  }

  postVerifyIdentityMail(
    memberVerifyIdentityDTO: MemberVerifyIdentityDTO,
  ): Promise<CommonResponseDTO<null>> {
    return this.fetchWrapper.postJson(
      `${this.serverUrl}/v1/m/p/members/password/reset`,
      DTO.toObject(memberVerifyIdentityDTO),
    ).then((response: CommonResponseJSON<null>) => CommonResponseDTO.ofNullData(response))
  }

  getVerifyIdentityInfo(
    oneTimeToken: string,
  ): Promise<CommonResponseDTO<Nullable<MemberVerifyIdentityInfoDTO>>> {
    return this.fetchWrapper.getByJson(
      `${this.serverUrl}/v1/m/p/members/password/reset/${oneTimeToken}`,
    ).then((response: CommonResponseJSON<MemberVerifyIdentityInfoJSON>) => {
      return CommonResponseDTO.from(
        response,
        MemberVerifyIdentityInfoDTO.fromJSON,
      )
    })
  }

  putResetPassword(
    oneTimeToken: string,
    resetPasswordDto: MemberResetPasswordDTO,
  ): Promise<CommonResponseDTO<null>> {
    return this.fetchWrapper.putJson(
      `${this.serverUrl}/v1/m/p/members/password/reset/${oneTimeToken}`,
      DTO.toObject(resetPasswordDto),
    ).then((response: CommonResponseJSON<null>) => CommonResponseDTO.ofNullData(response))
  }

  deleteCreditCard(cardSeq: string): Promise<CommonResponseDTO<null>> {
    return this.fetchWrapper.deleteJson(
      `${this.serverUrl}/v1/m/r/members/self/credit_cards/${cardSeq}`,
      withMembersAuth(),
    ).then((response: CommonResponseJSON<null>) => CommonResponseDTO.ofNullData(response))
  }

  postShippingAddress(shippingAddress: MemberShippingAddressDTO): Promise<CommonResponseDTO<null>> {
    return this.fetchWrapper.postJson(
      `${this.serverUrl}/v1/m/r/members/self/shipping_addresses`,
      {
        ...DTO.toObject(shippingAddress),
        sendMail: true,
      },
      withMembersAuth(),
    ).then((response: CommonResponseJSON<null>) => CommonResponseDTO.ofNullData(response))
  }

  getShippingAddressList(): Promise<CommonResponseDTO<Nullable<MemberShippingAddressInfoDTO[]>>> {
    return this.fetchWrapper.getByJson(
      `${this.serverUrl}/v1/m/r/members/self/shipping_addresses`, withMembersAuth(),
    ).then((response: CommonResponseJSON<ShippingAddressInfoJSON[]>) => {
      return CommonResponseDTO.fromArray(
        response,
        MemberShippingAddressInfoDTO.fromJSON,
      )
    })
  }

  putShippingAddress(
    shippingAddressInfo: MemberShippingAddressInfoDTO,
  ): Promise<CommonResponseDTO<null>> {
    const {shippingAddressId, ...rest} = shippingAddressInfo
    return this.fetchWrapper.putJson(
      `${this.serverUrl}/v1/m/r/members/self/shipping_addresses/${shippingAddressId}`,
      {
        ...DTO.toObject(rest),
        sendMail: true,
      },
      withMembersAuth(),
    ).then((response: CommonResponseJSON<null>) => CommonResponseDTO.ofNullData(response))
  }

  deleteShippingAddress(shippingAddressId: number): Promise<CommonResponseDTO<null>> {
    return this.fetchWrapper.deleteJson(
      `${this.serverUrl}/v1/m/r/members/self/shipping_addresses/${shippingAddressId}?sendMail=true`,
      withMembersAuth(),
    ).then((response: CommonResponseJSON<null>) => CommonResponseDTO.ofNullData(response))
  }

  postContinuanceCvs(
    bucketKey: string,
    convenienceStoreKey: ConvenienceStoreKey,
  ): Promise<CommonResponseDTO<null>> {
    return this.fetchWrapper.postJson(
      `${this.serverUrl}/v1/m/r/members/self/contracts/${bucketKey}/continuance_convenience_store`,
      {convenienceStoreId: convenienceStoreKey},
      withMembersAuth(),
    ).then((response: CommonResponseJSON<null>) => CommonResponseDTO.ofNullData(response))
  }

  getSignedUpBucketInfoList(): Promise<CommonResponseDTO<Nullable<SignedUpBucketInfoDTO[]>>> {
    return this.fetchWrapper.getByJson(`${this.serverUrl}/v1/m/r/members/self/buckets`, withMembersAuth())
      .then((response: CommonResponseJSON<SignedUpBucketInfoJSON[]>) => {
        return CommonResponseDTO.fromArray(
          response,
          SignedUpBucketInfoDTO.fromJSON,
        )
      })
  }

  getBucketBannerListBySignupKey(signupKey: string, bannerDiv: string)
    : Promise<CommonResponseDTO<BucketBannerDTO[]>> {
    return this.fetchWrapper.getByJson(`${this.serverUrl}/v1/m/p/banner/${signupKey}/${bannerDiv}`)
      .then((response: CommonResponseJSON<BucketBannerJSON[]>) => {
        return CommonResponseDTO.fromArray(
          response,
          BucketBannerDTO.fromJSON,
        )
      })
  }
}

export default NetworkMemberRepo
